class ProductTabs extends HTMLElement {
  constructor() {
    super();
    this.detailsList = this.querySelectorAll(
      ".cstm-product-tabs--details details"
    );
    this.collectionsLists = this.querySelectorAll(".cstm-product-tabs--item");
    this.collectionsContents = this.querySelector(
      ".cstm-product-tabs--content"
    );
    this.looxWidget = this.querySelector("#looxReviews");
    this.mobileContainer = this.querySelector(".mobile-reviews");
    this.desktopContainer = this.querySelector(".desktop-reviews");

    this.handleResize = this.handleResize.bind(this);
    this.handleTabClick = this.handleTabClick.bind(this);
    this.handleMobileTabClick = this.handleMobileTabClick.bind(this);

    // Singleton initialization flag
    this.isLooxInitialized = false;
    this.currentContainer = null;
    this.resizeObserver = null;
  }

  connectedCallback() {
    this.handleResize();
    window.addEventListener("resize", this.handleResize);

    if (this.detailsList.length > 0) {
      const firstTab = this.detailsList[0].dataset.id;
      this.chooseTab(firstTab);
    }

    // Initialize Loox widget singleton
    this.initializeLooxWidget();

    // ShowMore/ShowLess logic for product description
    this.setupShowMoreDescription();

    // Set up resize observer for the widget
    this.setupResizeObserver();
  }

  disconnectedCallback() {
    window.removeEventListener("resize", this.handleResize);
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }

  /**
   * Singleton Pattern: Initialize Loox widget only once
   */
  initializeLooxWidget() {
    if (this.isLooxInitialized || !this.looxWidget) {
      return;
    }

    try {
      // Wait for Loox to be available
      this.waitForLoox().then(() => {
        this.isLooxInitialized = true;
        console.log("Loox widget initialized successfully");
        
        // Move widget to appropriate container
        this.moveLooxWidget();
        
        // Set up mutation observer to handle dynamic content changes
        this.setupMutationObserver();
      }).catch((error) => {
        console.warn("Loox initialization failed:", error);
      });
    } catch (error) {
      console.error("Error initializing Loox widget:", error);
    }
  }

  /**
   * Wait for Loox to be available in the global scope
   */
  waitForLoox(timeout = 10000) {
    return new Promise((resolve, reject) => {
      const startTime = Date.now();
      
      const checkLoox = () => {
        // Check if Loox is available and widget has content
        if (this.looxWidget && this.looxWidget.innerHTML.trim() !== "") {
          resolve();
          return;
        }
        
        // Check for timeout
        if (Date.now() - startTime > timeout) {
          reject(new Error("Loox loading timeout"));
          return;
        }
        
        // Continue checking
        setTimeout(checkLoox, 100);
      };
      
      checkLoox();
    });
  }

  /**
   * Move Loox widget to appropriate container based on screen size
   */
  moveLooxWidget() {
    if (!this.looxWidget || !this.isLooxInitialized) {
      return;
    }

    const targetContainer = window.innerWidth > 1000 ? this.desktopContainer : this.mobileContainer;
    
    if (!targetContainer || this.currentContainer === targetContainer) {
      return;
    }

    try {
      // Clear previous container
      if (this.currentContainer) {
        this.currentContainer.innerHTML = "";
      }

      // Move widget to new container
      this.looxWidget.style.display = "block";
      targetContainer.appendChild(this.looxWidget);
      this.currentContainer = targetContainer;

      // Trigger any Loox refresh if needed
      this.refreshLooxWidget();
      
      console.log("Loox widget moved to:", window.innerWidth > 1000 ? "desktop" : "mobile");
    } catch (error) {
      console.error("Error moving Loox widget:", error);
    }
  }

  /**
   * Refresh Loox widget after moving (if needed)
   */
  refreshLooxWidget() {
    try {
      // Check if Loox has a refresh method
      if (window.Loox && typeof window.Loox.refresh === 'function') {
        window.Loox.refresh();
      }
      
      // Trigger resize event for potential sliders/carousels
      window.dispatchEvent(new Event('resize'));
    } catch (error) {
      console.warn("Could not refresh Loox widget:", error);
    }
  }

  /**
   * Setup mutation observer to handle dynamic content changes
   */
  setupMutationObserver() {
    if (!this.looxWidget) {
      return;
    }

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
          // Content was added to the widget, might need to refresh
          this.refreshLooxWidget();
        }
      });
    });

    observer.observe(this.looxWidget, {
      childList: true,
      subtree: true
    });
  }

  /**
   * Setup resize observer for the widget container
   */
  setupResizeObserver() {
    if (!window.ResizeObserver || !this.looxWidget) {
      return;
    }

    this.resizeObserver = new ResizeObserver((entries) => {
      for (let entry of entries) {
        // Widget size changed, trigger refresh
        this.refreshLooxWidget();
      }
    });

    this.resizeObserver.observe(this.looxWidget);
  }

  handleResize() {
    this.removeAllEventListeners();

    if (window.innerWidth > 1000) {
      this.setupDesktop();
    } else {
      this.setupMobile();
    }

    // Move Loox widget if needed
    if (this.isLooxInitialized) {
      this.moveLooxWidget();
    }
  }

  removeAllEventListeners() {
    this.detailsList.forEach((el) => {
      el.removeEventListener("click", this.handleTabClick);
      el.removeEventListener("click", () => this.handleMobileTabClick(el));
    });
  }

  setupDesktop() {
    if (this.collectionsContents) {
      this.collectionsContents.style.display = "";
    }

    this.detailsList.forEach((el) => {
      el.addEventListener("click", this.handleTabClick);
    });
  }

  setupMobile() {
    if (this.collectionsContents) {
      this.collectionsContents.style.display = "none";
    }

    this.detailsList.forEach((el) => {
      el.querySelector("summary").addEventListener("click", () =>
        this.handleMobileTabClick(el)
      );
    });
  }

  handleTabClick(event) {
    const el = event.currentTarget;
    this.chooseTab(el.dataset.id);
  }

  handleMobileTabClick(el) {
    this.clearOtherOpenDetails(el.dataset.id);
    this.scrollToEl(el);
  }

  chooseTab(id) {
    if (!id) return;

    this.detailsList.forEach((el) => {
      const isSelected = el.dataset.id === id;
      el.classList.toggle("open", isSelected);
      el.open = isSelected;
    });

    this.collectionsLists.forEach((el) => {
      el.classList.toggle("open", el.dataset.id === id);
    });

    // If reviews tab is selected, ensure widget is properly positioned
    if (id === "product-tabs--reviews" && this.isLooxInitialized) {
      setTimeout(() => {
        this.moveLooxWidget();
      }, 100);
    }
  }

  clearOtherOpenDetails(id) {
    if (!id) return;

    this.detailsList.forEach((el) => {
      if (el.dataset.id !== id) {
        el.open = false;
      }
    });
  }

  scrollToEl(el) {
    if (!el) return;

    const header = document.querySelector(".cstm-header");
    const offset = header ? header.scrollHeight + 20 : 20;
    const elementRect = el.getBoundingClientRect();
    const absoluteElementTop = elementRect.top + window.pageYOffset;

    window.scrollTo({
      top: absoluteElementTop - offset,
      behavior: "smooth",
    });
  }

  setupShowMoreDescription() {
    const descTexts = this.querySelectorAll(
      ".cstm-product-tab__description--text"
    );
    const showBtns = this.querySelectorAll(".cstm-product-tab__show-btn");

    if (!descTexts.length || !showBtns.length) return;

    descTexts.forEach((descText, index) => {
      const showBtn = showBtns[index];
      const readMore = showBtn.querySelector(".cstm-product-tab__read-more");
      const readLess = showBtn.querySelector(".cstm-product-tab__read-less");

      if (!readMore || !readLess) return;

      this.setButtonState = () => {
        descText.classList.remove("expanded");
        descText.classList.remove("truncated");
        showBtn.style.display = "none";
        descText.style.maxHeight = "";

        if (descText.scrollHeight > 400) {
          showBtn.style.display = "flex";
          descText.classList.add("truncated");
          readMore.style.display = "inline";
          readLess.style.display = "none";
        } else {
          showBtn.style.display = "none";
          descText.classList.remove("truncated");
          descText.style.maxHeight = "";
        }
      };

      showBtn.addEventListener("click", () => {
        if (descText.classList.contains("expanded")) {
          descText.classList.remove("expanded");
          descText.classList.add("truncated");
          readMore.style.display = "inline";
          readLess.style.display = "none";
        } else {
          descText.classList.add("expanded");
          descText.classList.remove("truncated");
          descText.style.maxHeight = "";
          readMore.style.display = "none";
          readLess.style.display = "inline";
        }
      });

      this.setButtonState();
    });

    window.addEventListener("resize", () => {
      descTexts.forEach((descText, index) => {
        const showBtn = showBtns[index];
        if (showBtn) {
          const readMore = showBtn.querySelector(
            ".cstm-product-tab__read-more"
          );
          const readLess = showBtn.querySelector(
            ".cstm-product-tab__read-less"
          );
          if (readMore && readLess) {
            this.setButtonState();
          }
        }
      });
    });
  }

  /**
   * Public API method to manually move widget to a specific container
   * Usage: document.querySelector('product-tabs').moveWidgetToContainer(targetElement)
   */
  moveWidgetToContainer(targetContainer) {
    if (!this.looxWidget || !targetContainer || !this.isLooxInitialized) {
      console.warn("Cannot move widget: not initialized or invalid target");
      return false;
    }

    try {
      // Clear current container
      if (this.currentContainer) {
        this.currentContainer.innerHTML = "";
      }

      // Move to new container
      this.looxWidget.style.display = "block";
      targetContainer.appendChild(this.looxWidget);
      this.currentContainer = targetContainer;

      // Refresh widget
      this.refreshLooxWidget();
      
      console.log("Loox widget manually moved to custom container");
      return true;
    } catch (error) {
      console.error("Error manually moving Loox widget:", error);
      return false;
    }
  }

  /**
   * Public API method to check if widget is initialized
   */
  isWidgetInitialized() {
    return this.isLooxInitialized;
  }

  /**
   * Public API method to get current widget container
   */
  getCurrentContainer() {
    return this.currentContainer;
  }
}

customElements.define("product-tabs", ProductTabs);
